import { Info } from 'lucide-react';
import { useState, type ReactElement, type ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface InfoTooltipProps {
  /** Names the thing being explained; becomes the trigger's accessible name. */
  label: string;
  tooltip: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

// MUI's Dialog sits at 1300 and Radix copies the content's computed z-index onto its positioned
// wrapper, so an SEO field tooltip inside the add-page modal needs MUI's own tooltip layer.
const TOOLTIP_Z_INDEX = 1500;

/**
 * The info icon that reveals an explanation of a setting or form field, on hover, on keyboard
 * focus, and on click. Shared by every labelled control in Plus Websites so the affordance and
 * its accessible name (`<label> info`) stay identical wherever an explanation is offered.
 */
export function InfoTooltip({ label, tooltip, side = 'right' }: InfoTooltipProps): ReactElement {
  const [open, setOpen] = useState(false);

  return (
    // Hoverable content would stay open while the pointer crosses it on the way to the next
    // field, leaving the explanation on top of the icon the user is aiming for. Closing as soon
    // as the pointer leaves the icon keeps every other trigger reachable in one click.
    <Tooltip open={open} onOpenChange={setOpen} disableHoverableContent>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${label} info`}
          // Radix closes a tooltip on click, so on its own a click only ever dismisses what
          // hovering had just opened. preventDefault suppresses that close handler, which is what
          // lets people click the icon instead of hovering it.
          onClick={(event) => {
            event.preventDefault();
            setOpen(true);
          }}
          className="vtwo:inline-flex vtwo:cursor-pointer vtwo:rounded vtwo:text-typography-secondary vtwo:outline-none vtwo:focus-visible:ring-2 vtwo:focus-visible:ring-ring"
        >
          <Info className="vtwo:size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        // Radix owns the positioned wrapper around this content and exposes no way to style it,
        // yet that wrapper is what swallows a click aimed at whatever the open tooltip overlaps —
        // in a stacked form, usually the next field's info icon. An explanation is never a click
        // target, so the whole layer opts out of hit testing.
        ref={(node: HTMLDivElement | null) => {
          if (node?.parentElement) node.parentElement.style.pointerEvents = 'none';
        }}
        side={side}
        style={{ zIndex: TOOLTIP_Z_INDEX }}
        className="vtwo:max-w-[320px] vtwo:rounded vtwo:bg-gray-900 vtwo:p-2.5 vtwo:text-xs vtwo:font-medium vtwo:leading-4 vtwo:text-white"
      >
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}
